import styled from 'styled-components';

import { AuditLogEntryFragmentFragment, PatchOp } from 'data/__generated__';

import { getAuditDisplay } from './audit-display.config';

type Patch = AuditLogEntryFragmentFragment['patches'][number];

export type AuditChange = {
    key: string;
    collectionName: string;
    /** Affiché devant le changement quand il ne vient PAS du document regardé, ex. « Emplacement (2,3) ». */
    collectionLabel?: string;
    patch: Patch;
};

const formatPatchValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }

    return String(value);
};

/** Le libellé métier du champ, ou le chemin brut — jamais rien. */
export const getFieldLabel = (collectionName: string, path: string): string =>
    getAuditDisplay(collectionName)?.fieldLabels?.[path] ?? path.replace(/^\//, '');

const OPERATION_ICONS: Record<PatchOp, string> = {
    [PatchOp.Add]: '+',
    [PatchOp.Remove]: '−',
    [PatchOp.Replace]: '→',
    [PatchOp.Move]: '↔',
    [PatchOp.Copy]: '⧉',
    [PatchOp.Test]: '?',
};

/**
 * Le détail d'un changement : le champ, puis la valeur AVANT et la valeur APRÈS encadrées.
 *
 * Partagé entre la section d'une page de détail et la page d'une ligne d'audit, pour que le même
 * changement se lise pareil aux deux endroits.
 */
export const AuditChangeList = ({ changes }: { changes: AuditChange[] }) => (
    <Container>
        {changes.map(({ key, collectionName, collectionLabel, patch }) => (
            <ChangeItem key={key}>
                <ChangeHeader>
                    <ChangeIcon>{OPERATION_ICONS[patch.op] ?? '•'}</ChangeIcon>
                    {collectionLabel ? <CollectionTag>{collectionLabel}</CollectionTag> : null}
                    <FieldName>{getFieldLabel(collectionName, patch.path)}</FieldName>
                </ChangeHeader>

                {patch.__typename === 'ReplacePatch' ? (
                    <Values>
                        <Value>
                            <ValueLabel>Avant</ValueLabel>
                            <ValueContent>{formatPatchValue(patch.oldValue)}</ValueContent>
                        </Value>
                        <Arrow>→</Arrow>
                        <Value>
                            <ValueLabel>Après</ValueLabel>
                            <ValueContent>{formatPatchValue(patch.value)}</ValueContent>
                        </Value>
                    </Values>
                ) : null}

                {patch.__typename === 'AddPatch' ? (
                    <Value>
                        <ValueLabel>Valeur ajoutée</ValueLabel>
                        <ValueContent>{formatPatchValue(patch.value)}</ValueContent>
                    </Value>
                ) : null}

                {patch.__typename === 'RemovePatch' ? (
                    <Value>
                        <ValueLabel>Valeur supprimée</ValueLabel>
                        <ValueContent>{formatPatchValue(patch.oldValue)}</ValueContent>
                    </Value>
                ) : null}

                {patch.__typename === 'MovePatch' || patch.__typename === 'CopyPatch' ? (
                    <Value>
                        <ValueLabel>Depuis</ValueLabel>
                        <ValueContent>{patch.from}</ValueContent>
                    </Value>
                ) : null}

                {patch.__typename === 'TestPatch' ? (
                    <Value>
                        <ValueLabel>Valeur testée</ValueLabel>
                        <ValueContent>{formatPatchValue(patch.value)}</ValueContent>
                    </Value>
                ) : null}
            </ChangeItem>
        ))}
    </Container>
);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ChangeItem = styled.div`
    padding: 6px 10px;
    border-radius: 4px;
    border-left: 3px solid ${({ theme }) => theme.ctaPrimaryColor};
`;

const ChangeHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;

const ChangeIcon = styled.span`
    font-weight: bold;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    font-size: 14px;
    min-width: 16px;
`;

const CollectionTag = styled.span`
    padding: 1px 6px;
    border-radius: var(--radius-md);
    background-color: ${({ theme }) => theme.lightBorderColor};
    font-size: 11px;
`;

const FieldName = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
`;

const Values = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Arrow = styled.span`
    color: ${({ theme }) => theme.ctaPrimaryColor};
    font-weight: bold;
    font-size: 14px;
`;

const Value = styled.div`
    flex: 1;
    min-width: 120px;
`;

const ValueLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    display: block;
    margin-bottom: 2px;
`;

const ValueContent = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
    background-color: ${({ theme }) => theme.backgroundColor};
    padding: 4px 6px;
    border-radius: 3px;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 80px;
    overflow-y: auto;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;
